import { combineLatest, concat, ignoreElements, map, merge, type Observable, of, scan, startWith, switchMap, timer } from 'rxjs';

import { filterNull } from './rxjs';

import { makePagination, type Pagination } from '@s/core';

/** Async pagination options. */
export type AsyncPaginationOptions<T> = {
  [K in keyof T]: Observable<T[K]>;
};

const QUERY_DEBOUNCE_TIME_MS = 500;

/**
 * Allows paginating data based on provided object containing async properties for pagination.
 * @param asyncOptions Async objects with pagination data.
 * @param fetch Fetch function that accepts the pagination options.
 * @example
 * ```ts
 * const searchString$ = new BehaviorSubject('');
 * const $ = new BehaviorSubject('');
 *
 * ```
 */
export function paginate<T, O>(
  asyncOptions: AsyncPaginationOptions<O>,
  fetch: (options: O) => Observable<Pagination<T>>,
): Observable<Pagination<T> | null> {
  return combineLatest(asyncOptions).pipe(
    switchMap(syncOptions => concat(

      // First, reset the state
      of(null),

      // Then, debounce the query
      timer(QUERY_DEBOUNCE_TIME_MS).pipe(ignoreElements()),

      // After debounce time, fetch the value
      fetch(syncOptions),
    )),
  );
}

const emptyPagination = makePagination({
  items: [],
  hasNext: false,
  hasPrev: false,
  totalCount: 0,
});

/**
 * Accumulate previous and current pagination items.
 * @param newPage New page.
 */
const onAdd = <T>(newPage: Pagination<T>) => (currentPage: Pagination<T>) => makePagination({
  hasNext: newPage.hasNext,
  hasPrev: newPage.hasPrev,
  totalCount: newPage.totalCount,
  items: currentPage.items.concat(newPage.items),
});

/** Clear pagination items. */
const onClear = () => () => emptyPagination;

/**
 * Accumulative pagination.
 * @param options Pagination options.
 * @param fetch Callback function for retrieving items.
 * @param clear$ Observable that emits whenever we need to clear accumulated data.
 */
export function accumulativePagination<
  TOptions extends Record<string, unknown>,
  TData extends Record<string, unknown>,
>(
  options: AsyncPaginationOptions<TOptions>,
  fetch: (options: TOptions) => Observable<Pagination<TData>>,
  clear$: Observable<unknown>,
): Observable<Pagination<TData> | null> {
  const page$ = paginate(options, fetch).pipe(filterNull());

  const onAdd$ = page$.pipe(map(onAdd));
  const onClear$ = clear$.pipe(map(onClear));

  return merge(onAdd$, onClear$).pipe(
    scan((acc: Pagination<TData>, handlePagination) => handlePagination(acc), emptyPagination),
    startWith(null),
  );
}
